import React, { useLayoutEffect, useMemo, useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Crown, ChevronRight } from "lucide-react";
import { resolveAsset } from "../lib/api";
import GroupAdvancementView from "./GroupAdvancementView";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function TeamRow({ team, score, winner, live, championTeamId }) {
  const isWin = winner && team && team.id === winner;
  const isChampion = championTeamId && team && team.id === championTeamId;
  return (
    <div
      className={`team-row ${isWin ? "winner" : team ? "loser" : ""} ${isChampion ? "champion" : ""}`}
      data-testid={isChampion ? `champion-${team.id}` : undefined}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isChampion && (
          <Crown
            size={14}
            className="text-yellow-300 drop-shadow-[0_0_6px_rgba(253,224,71,0.9)] shrink-0 champion-crown"
            fill="currentColor"
            strokeWidth={2}
          />
        )}
        {team?.logo_url ? (
          <img
            src={resolveAsset(team.logo_url)}
            alt=""
            className="team-logo object-cover"
            style={{ background: team.color || "#E60000" }}
          />
        ) : (
          <div className="team-logo" style={{ background: team?.color || "#2a2a2a" }}>
            {team ? initials(team.name) : "—"}
          </div>
        )}
        <span className="truncate font-medium">{team?.name || <span className="text-zinc-600">TBD</span>}</span>
      </div>
      <div className="flex items-center gap-2">
        {live && <span className="live-dot" />}
        <span className="score-box">{team ? score : "-"}</span>
      </div>
    </div>
  );
}

function MatchCard({ match, teamsById, matchRef, onClick, justUpdated, championTeamId }) {
  const ta = teamsById[match.team_a_id];
  const tb = teamsById[match.team_b_id];
  return (
    <motion.div
      ref={matchRef}
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      onClick={() => onClick && onClick(match)}
      data-testid={`match-${match.id}`}
      className={`match-card cursor-pointer ${match.status === "live" ? "live" : ""} ${match.bracket === "grand" ? "pulse-glow" : ""} ${justUpdated ? "just-updated" : ""}`}
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-white/5 flex justify-between items-center">
        <span>{match.round_name || `Match ${match.position + 1}`}</span>
        <div className="flex items-center gap-2">
          {justUpdated && (
            <span
              data-testid={`match-just-updated-${match.id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/50 text-red-200 text-[9px] font-bold tracking-[0.15em] just-updated-pulse"
            >
              <span className="live-dot" /> LIVE
            </span>
          )}
          {match.status === "live" && !justUpdated && (
            <span className="text-red-400 flex items-center gap-1"><span className="live-dot" />LIVE</span>
          )}
          {match.status === "completed" && !justUpdated && <span className="text-emerald-400">FINAL</span>}
        </div>
      </div>
      <TeamRow team={ta} score={match.score_a} winner={match.winner_id} live={match.status === "live"} championTeamId={championTeamId} />
      <TeamRow team={tb} score={match.score_b} winner={match.winner_id} live={match.status === "live"} championTeamId={championTeamId} />
    </motion.div>
  );
}

// Round-robin: simple grid, no connectors
function RoundRobinView({ matches, teamsById, onMatchClick, recentlyUpdated, championTeamId }) {
  return (
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {matches.map((m) => (
          <MatchCard
            key={m.id}
            match={m}
            teamsById={teamsById}
            matchRef={null}
            onClick={onMatchClick}
            justUpdated={Boolean(recentlyUpdated && recentlyUpdated[m.id])}
            championTeamId={championTeamId}
          />
        ))}
      </div>
    </div>
  );
}

function EliminationView({ matches, teamsById, onMatchClick, title, columnGap = 96, recentlyUpdated, championTeamId }) {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const matchRefs = useRef({});
  const [lines, setLines] = useState([]);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const rounds = useMemo(() => {
    const grouped = {};
    matches.forEach((m) => {
      const key = m.bracket === "grand" ? "grand" : `${m.bracket}-${m.round}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    });
    Object.values(grouped).forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return grouped;
  }, [matches]);

  const winnersRounds = Object.keys(rounds)
    .filter((k) => k.startsWith("winners-"))
    .sort((a, b) => Number(a.split("-")[1]) - Number(b.split("-")[1]))
    .map((k) => rounds[k]);

  const grandMatches = rounds["grand"] || [];

  const CARD_H = 88; // fixed card height (enforced via CSS) — keeps every column mathematically aligned
  const BASE_GAP = 28; // gap between sibling cards in round 0
  const UNIT = CARD_H + BASE_GAP; // vertical spacing unit = center-to-center distance in round 0

  const compute = () => {
    if (!innerRef.current) return;
    const containerBox = innerRef.current.getBoundingClientRect();
    const newLines = [];

    // Group source matches by their next_match_id so we draw ONE clean bracket per target
    const groups = {};
    matches.forEach((m) => {
      if (!m.next_match_id) return;
      const fromEl = matchRefs.current[m.id];
      const toEl = matchRefs.current[m.next_match_id];
      if (!fromEl || !toEl) return;
      if (!groups[m.next_match_id]) groups[m.next_match_id] = [];
      groups[m.next_match_id].push(m);
    });

    Object.entries(groups).forEach(([targetId, sources]) => {
      const targetEl = matchRefs.current[targetId];
      const tb = targetEl.getBoundingClientRect();
      const x2 = tb.left - containerBox.left;
      const y2 = tb.top + tb.height / 2 - containerBox.top;

      const infos = sources
        .map((m) => {
          const el = matchRefs.current[m.id];
          const fb = el.getBoundingClientRect();
          return {
            match: m,
            x: fb.right - containerBox.left,
            y: fb.top + fb.height / 2 - containerBox.top,
          };
        })
        .sort((a, b) => a.y - b.y);

      const sourceX = Math.max(...infos.map((s) => s.x));
      const midX = (sourceX + x2) / 2;

      if (infos.length === 1) {
        const s = infos[0];
        newLines.push({
          id: `solo-${s.match.id}`,
          d: `M ${s.x} ${s.y} H ${midX} V ${y2} H ${x2}`,
          isWinnerPath: Boolean(s.match.winner_id),
        });
        return;
      }

      // Two (or more) sources merging: draw clean bracket shape.
      // Per the spec:
      //   - Default: every segment GRAY.
      //   - Only the WINNER's path turns RED.
      //     Winner's path = winner's stub + spine segment between winner's Y and handoff Y + handoff.
      //   - Loser's stub + loser-side spine segment stay GRAY.
      const top = infos[0];
      const bot = infos[infos.length - 1];

      infos.forEach((s, idx) => {
        const didWin = Boolean(s.match.winner_id);
        newLines.push({
          id: `stub-${s.match.id}`,
          d: `M ${s.x} ${s.y} H ${midX}`,
          isWinnerPath: didWin,
        });
        // Spine segment between this source's Y and the handoff midpoint Y.
        // Use tiny y-offsets toward the center so the two halves butt against each other cleanly.
        const spineY = idx === 0 ? top.y : bot.y;
        newLines.push({
          id: `spine-${targetId}-${idx}`,
          d: `M ${midX} ${spineY} V ${y2}`,
          isWinnerPath: didWin,
        });
      });

      // Handoff into the target match: red if ANY source has a winner advancing.
      const anyWinner = infos.some((s) => s.match.winner_id);
      newLines.push({
        id: `hand-${targetId}`,
        d: `M ${midX} ${y2} H ${x2}`,
        isWinnerPath: anyWinner,
      });
    });

    setLines(newLines);
    setSize({ w: innerRef.current.scrollWidth, h: innerRef.current.scrollHeight });
  };

  useLayoutEffect(() => {
    compute();
    // Recompute after framer-motion layout animation settles
    // (match cards animate via the `layout` prop when data changes)
    const r1 = requestAnimationFrame(compute);
    const t1 = setTimeout(compute, 120);
    const t2 = setTimeout(compute, 360);
    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  useEffect(() => {
    const obs = new ResizeObserver(compute);
    if (innerRef.current) obs.observe(innerRef.current);
    window.addEventListener("resize", compute);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", compute);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderColumn = (col, colIdx, keyPrefix) => {
    // Mathematical bracket spacing:
    //   center spacing of round r = 2^r * UNIT  →  gap = 2^r * UNIT - CARD_H
    //   first card top offset = (2^r - 1) * UNIT / 2  (centers card r,0 midway between card r-1,0 and card r-1,1)
    const factor = Math.pow(2, colIdx);
    const gap = factor * UNIT - CARD_H;
    const marginTop = ((factor - 1) * UNIT) / 2;
    // Progress badge data (same look & feel as the Group Advancement view)
    const completedCount = col.filter((m) => m.status === "completed").length;
    const totalCount = col.length;
    const allDone = totalCount > 0 && completedCount === totalCount;
    // "Winner →" advance badge: shown on every round whose matches feed a
    // next match (i.e. a non-terminal round). The grand final has no
    // next_match_id, so it won't show the badge.
    const hasAdvance = col.some((m) => m.next_match_id);
    return (
      <div className="flex flex-col min-w-[240px]" key={`${keyPrefix}-${colIdx}`} style={{ paddingLeft: colIdx === 0 ? 0 : columnGap / 2, paddingRight: columnGap / 2 }}>
        <div className="mx-auto w-full max-w-[240px] pb-3 text-center text-[11px] uppercase tracking-[0.2em] text-red-400/90 font-mono font-semibold flex items-center justify-center gap-1.5 flex-wrap">
          <span className="truncate">{col[0]?.round_name || `Round ${colIdx + 1}`}</span>
          {totalCount > 1 && (
            <span
              data-testid={`${keyPrefix}-round-progress-${colIdx}`}
              className={`inline-flex items-center text-[9px] tracking-widest rounded-full px-1.5 py-0.5 border whitespace-nowrap
                ${allDone
                  ? "text-red-200 bg-red-500/15 border-red-500/40 round-complete-pulse"
                  : "text-zinc-400 bg-white/5 border-white/10"}`}
            >
              {completedCount}/{totalCount} done
            </span>
          )}
          {hasAdvance && (
            <span
              data-testid={`${keyPrefix}-round-advance-badge-${colIdx}`}
              className="inline-flex items-center gap-0.5 text-[9px] tracking-widest text-yellow-300/90 bg-yellow-400/10 border border-yellow-400/30 rounded-full px-1.5 py-0.5 whitespace-nowrap"
            >
              <ChevronRight size={9} /> Winner
            </span>
          )}
        </div>
        <div className="flex flex-col" style={{ gap: `${gap}px`, marginTop: `${marginTop}px` }}>
          {col.map((m) => (
            <MatchCard
              key={m.id}
              match={m}
              teamsById={teamsById}
              matchRef={(el) => { if (el) matchRefs.current[m.id] = el; else delete matchRefs.current[m.id]; }}
              onClick={onMatchClick}
              justUpdated={Boolean(recentlyUpdated && recentlyUpdated[m.id])}
              championTeamId={championTeamId}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="bracket-scroll">
      <div ref={innerRef} className="relative flex items-start p-6 min-w-max">
        <svg
          className="absolute inset-0 pointer-events-none"
          width={size.w}
          height={size.h}
          style={{ zIndex: 0 }}
        >
          <defs>
            <linearGradient id="line-gradient-active" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ff3333" stopOpacity="1" />
              <stop offset="100%" stopColor="#e60000" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          {lines.map((l) => (
            <path
              key={l.id}
              d={l.d}
              fill="none"
              stroke={l.isWinnerPath ? "#e60000" : "#555555"}
              strokeWidth={3}
              strokeLinejoin="miter"
              strokeLinecap="square"
              shapeRendering="crispEdges"
              style={{
                transition: "stroke 320ms ease",
                filter: l.isWinnerPath ? "drop-shadow(0 0 4px rgba(230,0,0,0.6))" : undefined,
              }}
            />
          ))}
        </svg>
        <div className="relative flex items-start" style={{ zIndex: 1 }}>
          {winnersRounds.map((col, idx) => renderColumn(col, idx, "w"))}
          {grandMatches.length > 0 && (
            <div className="flex flex-col min-w-[240px] justify-center" style={{ paddingLeft: columnGap / 2 }}>
              <div className="mx-auto w-full max-w-[240px] pb-3 text-center text-[11px] uppercase tracking-[0.2em] text-red-400/90 font-mono font-semibold">
                Grand Final
              </div>
              <div className="flex flex-col gap-6">
                {grandMatches.map((m) => (
                  <MatchCard
                    key={m.id}
                    match={m}
                    teamsById={teamsById}
                    matchRef={(el) => { if (el) matchRefs.current[m.id] = el; else delete matchRefs.current[m.id]; }}
                    onClick={onMatchClick}
                    justUpdated={Boolean(recentlyUpdated && recentlyUpdated[m.id])}
                    championTeamId={championTeamId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Bracket({ matches, teamsById, onMatchClick, format = "single_elimination", recentlyUpdated, championTeamId }) {
  if (format === "round_robin") {
    return <RoundRobinView matches={matches} teamsById={teamsById} onMatchClick={onMatchClick} recentlyUpdated={recentlyUpdated} championTeamId={championTeamId} />;
  }
  if (format === "group_advancement") {
    return (
      <GroupAdvancementView
        matches={matches}
        teamsById={teamsById}
        onMatchClick={onMatchClick}
        recentlyUpdated={recentlyUpdated}
        championTeamId={championTeamId}
      />
    );
  }

  const losers = matches.filter((m) => m.bracket === "losers");
  const others = matches.filter((m) => m.bracket !== "losers");

  return (
    <>
      <EliminationView matches={others} teamsById={teamsById} onMatchClick={onMatchClick} recentlyUpdated={recentlyUpdated} championTeamId={championTeamId} />
      {losers.length > 0 && (
        <div className="border-t border-white/5 mt-2">
          <div className="px-6 pt-5 pb-2 text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">
            Losers Bracket
          </div>
          <EliminationView matches={losers} teamsById={teamsById} onMatchClick={onMatchClick} recentlyUpdated={recentlyUpdated} championTeamId={championTeamId} />
        </div>
      )}
    </>
  );
}
